import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import Accordion from "@mui/joy/Accordion";
import AccordionDetails from "@mui/joy/AccordionDetails";
import AccordionGroup from "@mui/joy/AccordionGroup";
import AccordionSummary from "@mui/joy/AccordionSummary";
import Button from "@mui/joy/Button";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Textarea from "@mui/joy/Textarea";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { DeviceDto } from "./exhibits/dto/DeviceDto";
import { ExhibitionDto } from "./exhibits/dto/ExhibitionDto";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const AdminPage = () => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceDto[]>([]);
  const [exhibitions, setExhibitions] = useState<ExhibitionDto[]>([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedExhibition, setSelectedExhibition] = useState("");
  const [storyText, setStoryText] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTextSubmitEnabled, setIsTextSubmitEnabled] = useState(false);
  const [isAudioSubmitEnabled, setIsAudioSubmitEnabled] = useState(false);
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<any[]>([]);

  useEffect(() => {
    const isValidYear =
      yearOfBirth !== "" &&
      parseInt(yearOfBirth) > 1930 &&
      parseInt(yearOfBirth) < new Date().getFullYear();
    const hasText = storyText.trim() !== "";
    setIsTextSubmitEnabled(hasText && isValidYear);
  }, [yearOfBirth, storyText]);

  useEffect(() => {
    const isValidYear =
      yearOfBirth !== "" &&
      parseInt(yearOfBirth) > 1930 &&
      parseInt(yearOfBirth) < new Date().getFullYear();
    const hasAudio = audioUrl !== null && audioBlob !== null;
    setIsAudioSubmitEnabled(hasAudio && isValidYear);
  }, [yearOfBirth, audioUrl, audioBlob]);

  useEffect(() => {
    const saved = localStorage.getItem("auth_token");
    if (saved) setToken(saved);
  }, []);

  useEffect(() => {
    api
      .get("/api/exhibition/all")
      .then((res) => {
        setExhibitions(res.data);
        setSelectedExhibition(res.data[0].id.toString());
      })
      .catch((err) => {
        console.error("Error loading exhibitions", err);
        setError("Failed to load exhibitions.");
      });
  }, []);

  useEffect(() => {
    api
      .get(`/api/device/exhibition/${selectedExhibition}`)
      .then((res) => setDevices(res.data))
      .catch((err) => {
        console.error("Error loading devices", err);
        setError("Failed to load devices.");
      });
  }, [selectedExhibition]);

  const handleLogin = async (e: any) => {
    e.preventDefault();
    setError("");

    try {
      const res = await api.post("/api/user/login", { name, password });
      const token = res.data.access_token;

      localStorage.setItem("auth_token", token);
      setToken(token);
    } catch (error) {
      console.error("Login failed:", error);
      toast.error(`Error when logging in`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setToken(null);
  };

  const handleExhibitionChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
  ) => {
    setSelectedExhibition(newValue as string);
  };

  const handleDeviceChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
  ) => {
    setSelectedDevice(newValue as string);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStoryText(event.target.value);
  };

  const handleYearChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
  ) => {
    setYearOfBirth(newValue as string);
  };

  const renderYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1930; year--) {
      years.push(
        <Option value={year} key={year}>
          {year}
        </Option>,
      );
    }
    return years;
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorderRef.current = new MediaRecorder(stream);
    mediaRecorderRef.current.ondataavailable = (event: any) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };
    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      // Check the size of the audioBlob (https://vercel.com/docs/storage/vercel-blob)
      const sizeInMB = audioBlob.size / (1024 * 1024);
      if (sizeInMB > 4.5) {
        toast.error(
          "Audiodatei grösser als 5.6 MB. Bitte kürzere Aufnahme erstellen.",
        );
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(audioUrl);
      setAudioBlob(audioBlob);
    };

    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
    }
  };

  const submitAudio = async () => {
    if (!audioBlob) return;

    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.webm");
    formData.append("exhibitionId", selectedExhibition);

    try {
      const res = await api.post(
        `/api/user-story/submit-audio/device/${selectedDevice}/exhibition/${selectedExhibition}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const data = res.data;
      if (data.statusCode === 200) {
        toast.success(data.message);
        setAudioBlob(null);
        setAudioUrl("");
      } else {
        toast.error(data.message || "Server error while uploading audio.");
      }
    } catch (err: any) {
      console.error("Error uploading audio:", err);
      toast.error(`Error uploading audio: ${err.message || err}`);
    }
  };

  const submitText = async () => {
    const payload: any = {
      storyText: storyText.trim(),
    };

    try {
      const res = await api.post(
        `/api/user-story/submit-text/device/${selectedDevice}/exhibition/${selectedExhibition}`,
        payload,
      );
      const data = res.data;
      if (data.statusCode === 200) {
        toast.success(data.message);
        setStoryText("");
      } else {
        toast.error(data.message || "Server error while submitting text.");
      }
    } catch (err: any) {
      console.error("Error submitting text:", err);
      toast.error(`Error submitting text: ${err.message || err}`);
    }
  };

  const createDeviceNarrative = async () => {
    try {
      const response = await api.post(
        `/api/device-story/device/${selectedDevice}/exhibition/${selectedExhibition}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.statusCode === 200) {
        toast.success(response.data.message);
      } else if (response.data.statusCode === 500) {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(`Error creating device narrative: ${error}`);
    }
  };

  const createDeviceVisual = async () => {
    try {
      const response = await api.post(
        `/api/device-visual/device/${selectedDevice}/exhibition/${selectedExhibition}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.statusCode === 200) {
        toast.success(response.data.message);
      } else if (response.data.statusCode === 500) {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(`Error creating device visual: ${error}`);
    }
  };

  const createUserStoryVisuals = async () => {
    try {
      const response = await api.post(
        `/api/user-story/story-visuals/device/${selectedDevice}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.statusCode === 200) {
        toast.success(response.data.message);
      } else if (response.data.statusCode === 500) {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(`Error creating user story visuals: ${error}`);
    }
  };

  if (!token) {
    return (
      <Box sx={{ p: 4, maxWidth: 400, mx: "auto" }}>
        <ToastContainer position="bottom-center" autoClose={4000} />
        <Stack spacing={3}>
          <Typography level="h2">Admin Login</Typography>
          <form onSubmit={handleLogin}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Username</FormLabel>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </FormControl>
              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormControl>
              <Button type="submit" variant="solid" color="primary">
                Login
              </Button>
            </Stack>
          </form>
          {error && (
            <Typography level="body-sm" color="danger">
              {error}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
      <ToastContainer position="bottom-center" autoClose={4000} />
      <Stack spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography level="h2">AYO!</Typography>
          <Button variant="outlined" color="neutral" onClick={handleLogout}>
            Logout
          </Button>
        </Stack>

        {error && (
          <Typography level="body-sm" color="danger">
            {error}
          </Typography>
        )}

        <FormControl>
          <FormLabel>Exhibition</FormLabel>
          <Select
            value={selectedExhibition}
            onChange={handleExhibitionChange}
            placeholder="Select an exhibition"
          >
            {exhibitions.map((e) => (
              <Option key={e.id} value={e.id.toString()}>
                {e.title} ({e.description} | {e.language} | {e.location})
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Device</FormLabel>
          <Select
            value={selectedDevice}
            onChange={handleDeviceChange}
            placeholder="Select a device"
          >
            {devices.map((d) => (
              <Option key={d.id} value={d.id.toString()}>
                {d.name}
              </Option>
            ))}
          </Select>
        </FormControl>

        <Stack spacing={2}>
          <Typography level="h4">Admin Actions</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="solid"
              color="primary"
              onClick={createDeviceNarrative}
              disabled={selectedDevice === ""}
            >
              Create Device Narrative
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={createDeviceVisual}
              disabled={selectedDevice === ""}
            >
              Create Device Visual
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={createUserStoryVisuals}
              disabled={selectedDevice === ""}
            >
              Create User Story Visuals
            </Button>
          </Stack>
        </Stack>

        <FormControl>
          <FormLabel>Year of Birth</FormLabel>
          <Select
            value={yearOfBirth}
            onChange={handleYearChange}
            placeholder="Select year of birth"
          >
            {renderYearOptions()}
          </Select>
          <FormHelperText>
            Required to contextualize contributions historically
          </FormHelperText>
        </FormControl>

        <AccordionGroup size="md">
          <Accordion>
            <AccordionSummary>
              <Typography level="title-md">Write Text</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Textarea
                  minRows={5}
                  value={storyText}
                  onChange={handleTextChange}
                  placeholder="Type your story here..."
                />
                <Button
                  variant="solid"
                  color="primary"
                  onClick={submitText}
                  disabled={!isTextSubmitEnabled}
                >
                  Upload Text
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary>
              <Typography level="title-md">Record Audio</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {isRecording ? (
                  <Button
                    variant="outlined"
                    color="danger"
                    onClick={stopRecording}
                  >
                    Stop Recording
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={startRecording}
                  >
                    Start Recording
                  </Button>
                )}
                {audioUrl && (
                  <Box>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      Audio Preview
                    </Typography>
                    <audio controls src={audioUrl} style={{ width: "100%" }} />
                  </Box>
                )}
                <Button
                  variant="solid"
                  color="primary"
                  onClick={submitAudio}
                  disabled={!isAudioSubmitEnabled}
                >
                  Upload Audio
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>
      </Stack>
    </Box>
  );
};

export default AdminPage;
